import React, { useRef, useState } from 'react';

interface FileUploadProps {
    onFileSelect: (file: File | null) => void;
    currentFile?: File | null;
    existingFileUrl?: string | null; // URL de archivo ya guardado en el servidor
    existingFileName?: string; // Nombre para mostrar del archivo existente
    disabled?: boolean;
    acceptedTypes?: string;
    maxSize?: number; // en MB
    placeholder?: string;
    className?: string;
    showPreview?: boolean; // Si es imagen, mostrar preview
}

const FileUpload: React.FC<FileUploadProps> = ({
    onFileSelect,
    currentFile,
    existingFileUrl,
    existingFileName,
    disabled = false,
    acceptedTypes = "*/*",
    maxSize = 2,
    placeholder = "Haz click para cargar o arrastra el archivo",
    className = "",
    showPreview = false
}) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [dragOver, setDragOver] = useState(false);

    const handleUpload = () => {
        if (!disabled) {
            inputRef.current?.click();
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files?.[0];
        if (selectedFile) {
            // Validar tamaño
            if (selectedFile.size > maxSize * 1024 * 1024) {
                alert(`El archivo es demasiado grande. Tamaño máximo: ${maxSize}MB`);
                return;
            }
            
            // Crear preview si es imagen
            if (showPreview && selectedFile.type.startsWith('image/')) {
                const url = URL.createObjectURL(selectedFile);
                setPreviewUrl(url);
            }
            
            onFileSelect(selectedFile);
        } else {
            setPreviewUrl(null);
            onFileSelect(null);
        }
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(false);
        
        const files = Array.from(e.dataTransfer.files);
        if (files.length > 0) {
            const file = files[0];
            
            // Validar tamaño
            if (file.size > maxSize * 1024 * 1024) {
                alert(`El archivo es demasiado grande. Tamaño máximo: ${maxSize}MB`);
                return;
            }
            
            // Crear preview si es imagen
            if (showPreview && file.type.startsWith('image/')) {
                const url = URL.createObjectURL(file);
                setPreviewUrl(url);
            }
            
            onFileSelect(file);
        }
    };

    const removeFile = () => {
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
        }
        setPreviewUrl(null);
        onFileSelect(null);
        if (inputRef.current) {
            inputRef.current.value = '';
        }
    };

    const formatFileSize = (bytes: number) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    return (
        <div className={`file-upload-container ${className}`}>
            <input
                ref={inputRef}
                onChange={handleFileChange}
                className="file-input"
                type="file"
                disabled={disabled}
                accept={acceptedTypes}
                id="fileUpload"
            />
            
            <div 
                className={`file-upload-area ${dragOver ? 'drag-over' : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            >
                {currentFile ? (
                    // Archivo nuevo seleccionado
                    <div className="file-preview">
                        {showPreview && previewUrl && (
                            <div className="image-preview">
                                <img src={previewUrl} alt="Preview" className="preview-image" />
                            </div>
                        )}
                        <div className="file-info">
                            <span className="file-name">
                                <i className="fas fa-file me-2"></i>
                                {currentFile.name}
                            </span>
                            <span className="file-size">
                                {formatFileSize(currentFile.size)} de {maxSize}MB
                            </span>
                        </div>
                        <button 
                            type="button" 
                            className="remove-file-btn"
                            onClick={removeFile}
                            disabled={disabled}
                        >
                            <i className="fas fa-times"></i>
                        </button>
                    </div>
                ) : existingFileUrl ? (
                    // Archivo ya guardado en el servidor
                    <div className="file-preview existing-file">
                        <div className="file-info">
                            <span className="file-name">
                                <i className="fas fa-file-pdf me-2 text-danger"></i>
                                {existingFileName || 'Archivo guardado'}
                            </span>
                            <span className="file-size text-success">
                                <i className="fas fa-check-circle me-1"></i>
                                Guardado en el servidor
                            </span>
                        </div>
                        <a 
                            href={existingFileUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="view-file-btn"
                            title="Ver archivo"
                        >
                            <i className="fas fa-external-link-alt"></i>
                        </a>
                        <button 
                            type="button" 
                            className="change-file-btn"
                            onClick={handleUpload}
                            disabled={disabled}
                            title="Cambiar archivo"
                        >
                            <i className="fas fa-sync-alt"></i>
                        </button>
                    </div>
                ) : (
                    // Sin archivo
                    <label htmlFor="fileUpload" className="file-upload-label" onClick={handleUpload}>
                        <i className="fas fa-upload"></i>
                        <span>{placeholder} (máx. {maxSize}MB)</span>
                    </label>
                )}
            </div>

            <style>{`
                .file-upload-container {
                    width: 100%;
                }

                .file-input {
                    display: none;
                }

                .file-upload-area {
                    border: 2px dashed #e5e7eb;
                    border-radius: 12px;
                    padding: 2rem;
                    text-align: center;
                    transition: all 0.3s ease;
                    background: #ffffff;
                    cursor: pointer;
                }

                .file-upload-area:hover {
                    border-color: #3365ED;
                    background: #f8f9fa;
                }

                .file-upload-area.drag-over {
                    border-color: #3365ED;
                    background: #f0f4ff;
                }

                .file-upload-label {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 0.5rem;
                    cursor: pointer;
                    color: #6b7280;
                    font-size: 1rem;
                    margin: 0;
                }

                .file-upload-label i {
                    font-size: 2rem;
                    color: #3365ED;
                }

                .file-preview {
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    text-align: left;
                }

                .image-preview {
                    flex-shrink: 0;
                }

                .preview-image {
                    width: 60px;
                    height: 60px;
                    object-fit: cover;
                    border-radius: 8px;
                    border: 2px solid #e5e7eb;
                }

                .file-info {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    gap: 0.25rem;
                }

                .file-name {
                    font-weight: 500;
                    color: #374151;
                    font-size: 0.9rem;
                    display: flex;
                    align-items: center;
                }

                .file-size {
                    color: #6b7280;
                    font-size: 0.8rem;
                }

                .remove-file-btn {
                    background: #ef4444;
                    color: white;
                    border: none;
                    border-radius: 50%;
                    width: 32px;
                    height: 32px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    transition: background-color 0.2s ease;
                    flex-shrink: 0;
                }

                .remove-file-btn:hover {
                    background: #dc2626;
                }

                .remove-file-btn:disabled {
                    background: #9ca3af;
                    cursor: not-allowed;
                }

                .existing-file {
                    background: #f0fdf4;
                    border: 1px solid #22c55e;
                    border-radius: 8px;
                    padding: 0.75rem;
                }

                .view-file-btn {
                    background: #3365ED;
                    color: white;
                    border: none;
                    border-radius: 50%;
                    width: 32px;
                    height: 32px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    transition: background-color 0.2s ease;
                    flex-shrink: 0;
                    text-decoration: none;
                }

                .view-file-btn:hover {
                    background: #2554d4;
                    color: white;
                }

                .change-file-btn {
                    background: #f59e0b;
                    color: white;
                    border: none;
                    border-radius: 50%;
                    width: 32px;
                    height: 32px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    transition: background-color 0.2s ease;
                    flex-shrink: 0;
                }

                .change-file-btn:hover {
                    background: #d97706;
                }

                .change-file-btn:disabled {
                    background: #9ca3af;
                    cursor: not-allowed;
                }
            `}</style>
        </div>
    );
};

export default FileUpload;
